<script lang="ts">
  import { goto } from "$app/navigation";

  import type { SvelteHTMLElements } from "svelte/elements";

  import { useGlobalHotkey } from "$lib/design-systems/product/foundations/global-hotkey.svelte.ts";
  import Markdown from "$lib/design-systems/product/primitives/typography/Markdown.svelte";
  import { themeStore } from "$routes/(microsites)/gpu-glossary/_lib/gpu-glossary";
  import type { Page } from "$routes/(microsites)/gpu-glossary/_lib/gpu-glossary";
  import type { PageData } from "./$types";
  import GlossaryCodeSnippet from "./_components/GlossaryCodeSnippet.svelte";
  import GlossaryImage from "./_components/GlossaryImage.svelte";
  import GlossaryLink from "./_components/GlossaryLink.svelte";
  import MarketingFooter from "./_components/MarketingFooter.svelte";
  import SectionContents from "./_components/SectionContents.svelte";
  import { rehypeAddPreLang } from "./_lib/markdown/rehypeLangAttr";
  import { preserveAltMdPlugin } from "./_lib/markdown/remarkImgAltMd";

  export let data: PageData;

  let showFeedbackBox = false;
  $: isLightTheme = $themeStore.name === "light";

  const toggleFeedbackBox = () => {
    showFeedbackBox = !showFeedbackBox;
  };

  const goToPage = (page: Page | null) => {
    if (page) {
      goto(page.href);
    }
  };

  const hotKeys = [
    { hotKey: "ArrowLeft", action: () => goToPage(data.prevPage) },
    { hotKey: "ArrowRight", action: () => goToPage(data.nextPage) },
    { hotKey: "ArrowUp", action: () => goToPage(data.prevPage) },
    { hotKey: "ArrowDown", action: () => goToPage(data.nextPage) },
  ];

  useGlobalHotkey({ key: { hotkey: "?", shift: true } }, toggleFeedbackBox);
  hotKeys.forEach(({ hotKey, action }) => {
    useGlobalHotkey({ key: { hotkey: hotKey } }, action);
  });
</script>

<div
  class="flex min-h-full flex-col px-6 pt-4 sm:px-14 sm:pt-8"
  style:background-color={$themeStore.secondary[100]}
>
  <div class="flex flex-1 flex-col">
    <div class="flex flex-row justify-between">
      <div
        class="glossary-font-secondary mb-3"
        style:color={$themeStore.primary[60]}
      >
        {data.metadata.path}
      </div>
      <div class="relative">
        <a
          href="https://github.com/modal-labs/gpu-glossary/issues/new"
          target="_blank"
          rel="noopener noreferrer"
          class="glossary-font-secondary flex h-8 w-8 shrink-0 items-center justify-center rounded-full border"
          style:background-color={$themeStore.primary[20]}
          style:border-color={$themeStore.primary[100]}
          on:mouseenter={() => (showFeedbackBox = true)}
          on:mouseleave={() => (showFeedbackBox = false)}
        >
          ?
        </a>
        <div
          class="fade-blur pointer-events-none absolute top-[14px] right-9 z-10 border p-2.5 leading-[18px] whitespace-nowrap"
          style:border-color={$themeStore.primary[100]}
          style:background-color={$themeStore.secondary[100]}
          class:visible={showFeedbackBox}
        >
          Something seem wrong?<br />Or want to contribute?<br /><br />Click
          this button to<br />let us know on GitHub.
        </div>
      </div>
    </div>
    <div class="flex items-center gap-2 pb-4">
      <h1 class="text-3xl font-medium">{data.metadata.title}</h1>
      {#if data.metadata.abbreviation}
        <div
          class="h-fit rounded-xs px-2 text-2xl"
          style:color={$themeStore.primary[80]}
          style:background-color={$themeStore.primary[5]}
        >
          {data.metadata.abbreviation}
        </div>
      {/if}
    </div>
    <div class="flex flex-col gap-y-3" style:color={$themeStore.primary[80]}>
      <Markdown
        md={data.content}
        plugins={[rehypeAddPreLang, preserveAltMdPlugin]}
      >
        {#snippet a(props: SvelteHTMLElements["a"])}
          <GlossaryLink figCaptionSize="lg" {...props} />
        {/snippet}
        {#snippet img(props: SvelteHTMLElements["img"])}
          <GlossaryImage {...props}></GlossaryImage>
        {/snippet}
        {#snippet pre(props: SvelteHTMLElements["pre"])}
          <GlossaryCodeSnippet {...props}></GlossaryCodeSnippet>
        {/snippet}
        {#snippet code({ children, ...props }: SvelteHTMLElements["code"])}
          <!-- So that we can style in a non-global context -->
          <code {...props}>
            {@render children?.()}
          </code>
        {/snippet}
        {#snippet ul(props: SvelteHTMLElements["ul"])}
          {@const { children } = props}
          <ul class="ml-4 list-disc">{@render children?.()}</ul>
        {/snippet}
      </Markdown>
    </div>
    {#if data.isTopLevel && data.children}
      <SectionContents contents={data.children} />
    {/if}
  </div>
  <!-- Footer CTA -->
  <MarketingFooter {isLightTheme} />
  <!-- Previous/Next Page Navigation -->
  <div
    class="sticky bottom-0 z-5 mt-6 flex items-end justify-between pt-2 pb-8 text-sm sm:static sm:pt-0 sm:text-base"
    style:background-color={$themeStore.secondary[100]}
  >
    {#if data.prevPage}
      <a
        href={data.prevPage.href}
        class="flex max-w-[50%] items-center gap-2 self-center"
        ><svg
          width="20"
          height="16"
          viewBox="0 0 20 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.75 13H8.6667V10.8334L6.5 10.8334V8.66672L19.5 8.66672V6.50002L6.5 6.50002V4.33332H8.6667V2.16672H9.75V1.71661e-05H6.5V2.16672H4.3333V4.33332H2.1667V5.41672H1.0833V6.50002H0V8.66672H1.0833V9.75002L2.1667 9.75002V10.8334H4.3333V13H6.5V15.1667H9.75V13Z"
            fill="currentColor"
          />
        </svg>
        <span class="hidden sm:block">{data.prevPage.title}</span></a
      >
    {:else}
      <div></div>
    {/if}
    {#if data.nextPage}
      <a href={data.nextPage.href} class="flex items-center gap-2"
        ><span class="hidden sm:block">{data.nextPage.title}</span><svg
          width="20"
          height="16"
          viewBox="0 0 20 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.75 2.16666H10.8333V4.33333H13V6.49997H0L0 8.66667H13V10.8334H10.8333V13H9.75V15.1667H13V13H15.1667V10.8334H17.3333V9.74997H18.4167V8.66667H19.5V6.49997H18.4167V5.41667H17.3333V4.33333H15.1667V2.16666H13V0L9.75 0V2.16666Z"
            fill="currentColor"
          />
        </svg>
      </a>
    {/if}
  </div>
</div>

<style>
  .fade-blur {
    opacity: 0;
    filter: blur(10px);
    transition:
      opacity 0.5s ease,
      filter 0.5s ease,
      transform 0.5s ease;
  }

  .fade-blur.visible {
    opacity: 1;
    filter: blur(0);
    transform: translateY(-14px);
  }

  code {
    font-family:
      "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas,
      "Courier New", monospace;
  }
</style>
